import { FilterMethod, ICustomFilter } from '../../ui/data-table/components/filters/filters.models';
import { createRequestFilters } from '../../ui/data-table/data-table.service';
import type { FileCabinetListKey, FileCabinetListRequest } from './file-cabinet.types';
import { FILE_CABINET_VIEWS, FileCabinetSearch, FileCabinetView } from './file-cabinet.views';

/**
 * Table state → one list call. Filters go through the same builder as every other table, so their
 * wire values carry the operator the API requires — a bare value is rejected 400 "missing operator"
 * (sandbox, 2026-10-22).
 */

/** What the core table reports through `onFetch`. */
export interface FileCabinetTableRequest {
    pageIndex: number;
    pageSize: number;
    sortBy: { id: string; desc?: boolean }[];
    validFilters: ICustomFilter[];
}

export function fileCabinetListRequest(view: FileCabinetView, request: FileCabinetTableRequest): FileCabinetListRequest {
    const [sort] = request.sortBy;
    const column = sort ? view.columns.find(c => c.key === sort.id && c.sortKey) : undefined;
    return {
        limit: request.pageSize,
        offset: request.pageIndex * request.pageSize,
        sort: column ? column.sortKey : view.defaultSort.sortKey,
        order: column ? (sort.desc ? 'DESCENDING' : 'ASCENDING') : view.defaultSort.order,
        // A copy: the builder appends a default `filterActive` to the array it is given unless told
        // not to, and these endpoints have no such filter. The view's fixed filters (the call method)
        // are the source's to add: `fileCabinetWireFilters`.
        filters: fileCabinetDateRanges(view, createRequestFilters([...request.validFilters], true)),
    };
}

/**
 * The filters a list request goes out with: the caller's, then what makes the table what it is — the
 * call method of `call.in`/`out`/`other`. Last, so nothing the user filters by can replace it. Every
 * data source applies it in `list`, so a caller cannot get every call by asking for the inbound ones;
 * `call.all` is every call, on purpose.
 */
export function fileCabinetWireFilters(list: FileCabinetListKey, filters: Record<string, string[]>): Record<string, string[]> {
    const fixed = list === 'call.all' ? undefined : FILE_CABINET_VIEWS[list].fixedFilters;
    return { ...filters, ...(fixed ?? {}) };
}

/**
 * Two rows on one date — "Created after A" and "Created before B" — as the range they mean. The API
 * ORs repeated parameters (sandbox, 2026-10-22: GTE A + LTE B on /document = all 1200, not the 899
 * between), so sent as two they would widen the list instead of narrowing it. One `BT:` with full
 * ISO times is the range (899; a one-month range = GTE start − GTE end, 22 = 838 − 816). `BT:` with
 * bare dates — the shared DataTable's DateRange format — is ignored by the API (1200), so it is not
 * used. BT's ends are inclusive; a strict GT/LT differs only at the exact millisecond. Any other
 * combination of rows on one date is sent as it is, and ORed by the API.
 */
export function fileCabinetDateRanges(view: FileCabinetView, filters: Record<string, string[]>): Record<string, string[]> {
    const out = { ...filters };
    for (const { filterId, kind } of view.filters) {
        const values = out[filterId];
        if (kind !== 'date' || values?.length !== 2) {
            continue;
        }
        const lower = values.find(v => /^GTE?:/.test(v));
        const upper = values.find(v => /^LTE?:/.test(v));
        if (lower && upper) {
            out[filterId] = [`BT:${lower.slice(lower.indexOf(':') + 1)}:${upper.slice(upper.indexOf(':') + 1)}`];
        }
    }
    return out;
}

/**
 * The search box as a table filter. `hidden`: the box shows it, so it is not repeated as a chip;
 * `locked`: the Filters panel cannot remove it behind the box's back. Undefined when the box is
 * empty — an empty filter would still reset the table.
 */
export function fileCabinetSearchFilter(search: FileCabinetSearch, text: string): ICustomFilter | undefined {
    const value = searchValue(search.mode, text);
    if (!value) {
        return undefined;
    }
    const common = { filterId: search.filterId, hidden: true, locked: true };
    switch (search.mode) {
        case 'fts':
        case 'transcript':
            // `FT` is sent as typed — `FTS:<query>` — so MySQL boolean operators (+word -word "a b") work.
            return { ...common, filterType: 'FullText', filterMethod: FilterMethod.FT, value };
        case 'filename':
            return { ...common, filterType: 'String', filterMethod: FilterMethod.LK, value };
        case 'claim':
        default:
            return { ...common, filterType: 'String', filterMethod: FilterMethod.EQ, value };
    }
}

/**
 * The API reads a comma inside a filter value as the start of another value, and rejects the part
 * after it for having no operator — `filterOcr=FTS:doe, jane` is a 400 (sandbox, 2026-10-22). There
 * is no escape, so a comma never reaches the wire:
 * - full text: a space instead. MySQL boolean mode already treats the comma as a word separator, so
 *   `doe, jane` and `doe jane` mean the same.
 * - filename ("contains"): the longest comma-free part. Its matches include every filename that
 *   contains the whole text, so nothing that should match is lost.
 * - claim id: a UUID has no commas or spaces; both are dropped.
 */
export function searchValue(mode: FileCabinetView['search']['mode'], text: string): string {
    const trimmed = text.trim();
    switch (mode) {
        case 'fts':
        case 'transcript':
            return trimmed.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
        case 'filename':
            return trimmed
                .split(',')
                .map(part => part.trim())
                .reduce((longest, part) => (part.length > longest.length ? part : longest), '');
        case 'claim':
        default:
            return trimmed.replace(/[,\s]/g, '');
    }
}

/**
 * The table's filters with the screen's own (search, type shortcut) swapped in. The user's
 * Filters-panel entries are kept as they are — replacing the whole list, as a screen with no panel
 * can, would drop them every time the search box changed.
 */
export function fileCabinetMergeFilters(current: ICustomFilter[], owned: ICustomFilter[]): ICustomFilter[] {
    return [...current.filter(filter => !filter.hidden), ...owned];
}

/** A document-type shortcut as a table filter; hidden and locked for the same reasons as search. */
export function fileCabinetDocumentTypeFilter(type: string | undefined): ICustomFilter | undefined {
    if (!type) {
        return undefined;
    }
    return {
        filterId: 'filterDocumentType',
        filterType: 'String',
        filterMethod: FilterMethod.EQ,
        value: type,
        hidden: true,
        locked: true,
    };
}
